import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import { api, formatApiError, API_URL } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";
import { LogOut, LayoutDashboard, Inbox, Package, Sparkles, Users, MessageSquare, HelpCircle, Settings, Search, Trash2, Plus, Edit3, Image as ImageIcon } from "lucide-react";
import ImageUploadField from "@/components/admin/ImageUploadField";
import MediaLibrary from "@/components/admin/MediaLibrary";

const IMAGE_KEYS = new Set(["photo_url", "logo_url", "og_image"]);
// Map endpoint or settings section → crop preset so uploads land at a consistent aspect ratio.
const PRESET_MAP = {
  founders: "founder",
  testimonials: "testimonial",
  seo: "og",
  programs: "program",
};

const TABS = [
  { key: "summary", label: "Overview", icon: LayoutDashboard, testid: ADMIN.navSummary },
  { key: "enquiries", label: "Enquiries", icon: Inbox, testid: ADMIN.navEnquiries },
  { key: "programs", label: "Programs", icon: Package, testid: ADMIN.navPrograms },
  { key: "skills", label: "Skills", icon: Sparkles, testid: ADMIN.navSkills },
  { key: "founders", label: "Founders", icon: Users, testid: ADMIN.navFounders },
  { key: "testimonials", label: "Testimonials", icon: MessageSquare, testid: ADMIN.navTestimonials },
  { key: "faqs", label: "FAQs", icon: HelpCircle, testid: ADMIN.navFaqs },
  { key: "media", label: "Media Library", icon: ImageIcon, testid: "admin-nav-media" },
  { key: "site", label: "Site Settings", icon: Settings, testid: ADMIN.navSite },
  { key: "seo", label: "SEO", icon: Search, testid: ADMIN.navSeo },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("summary");
  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="hidden md:flex flex-col w-64 bg-[#0B132B] text-slate-200 sticky top-0 h-screen">
        <div className="p-5 border-b border-white/5">
          <div className="font-display text-lg text-white">Be Hire Ready</div>
          <div className="text-[10px] tracking-widest uppercase text-slate-400">Admin CMS</div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={t.testid}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${tab === t.key ? "bg-white/10 text-white" : "hover:bg-white/5"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/5">
          <div className="text-xs text-slate-400 truncate">{user?.email}</div>
          <button onClick={logout} data-testid={ADMIN.logout}
                  className="mt-3 w-full inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-xs">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="md:hidden flex items-center justify-between p-4 border-b bg-white">
          <div className="font-display text-[#0B132B]">BHR Admin</div>
          <select value={tab} onChange={(e) => setTab(e.target.value)} className="text-sm rounded-lg ring-1 ring-slate-200 px-2 py-1.5">
            {TABS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </div>
        <div className="p-5 sm:p-8">
          {tab === "summary" && <Summary />}
          {tab === "enquiries" && <Enquiries />}
          {tab === "programs" && <CrudSection endpoint="programs" fields={PROG_FIELDS} title="Programs" />}
          {tab === "skills" && <CrudSection endpoint="skills" fields={SKILL_FIELDS} title="Skills" />}
          {tab === "founders" && <CrudSection endpoint="founders" fields={FOUNDER_FIELDS} title="Founders" />}
          {tab === "testimonials" && <CrudSection endpoint="testimonials" fields={TEST_FIELDS} title="Testimonials" />}
          {tab === "faqs" && <CrudSection endpoint="faqs" fields={FAQ_FIELDS} title="FAQs" />}
          {tab === "media" && <MediaLibrary />}
          {tab === "site" && <SettingsForm endpoint="/content/site" title="Site Settings" fields={SITE_FIELDS} />}
          {tab === "seo" && <SettingsForm endpoint="/content/seo" title="SEO Settings" fields={SEO_FIELDS} />}
        </div>
      </main>
    </div>
  );
}

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl bg-white ring-1 ring-slate-200 p-5 ${className}`}>{children}</div>;
}

function Summary() {
  const [s, setS] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/admin/summary").then((r) => { if (!cancelled) setS(r.data); });
    return () => { cancelled = true; };
  }, []);
  if (!s) return <div className="text-slate-500">Loading…</div>;
  const items = [
    ["Total Enquiries", s.total_enquiries],
    ["New Enquiries", s.new_enquiries],
    ["Student Enquiries", s.student_enquiries],
    ["College Enquiries", s.college_enquiries],
    ["Employer Enquiries", s.employer_enquiries],
    ["Testimonials", s.testimonials],
    ["Active Programs", s.active_programs],
  ];
  return (
    <div>
      <div>
        <div className="text-xs tracking-widest uppercase text-slate-500">Dashboard</div>
        <h1 className="font-display text-3xl text-[#0B132B]">Welcome back</h1>
      </div>
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {items.map(([k, v]) => (
          <Card key={k}>
            <div className="text-[11px] uppercase tracking-widest text-slate-500">{k}</div>
            <div className="mt-2 font-display text-3xl text-[#0B132B]">{v}</div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function Enquiries() {
  const [list, setList] = useState([]);
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (type) params.type = type;
    if (status) params.status = status;
    const { data } = await api.get("/enquiries", { params });
    setList(data); setLoading(false);
  }, [type, status]);
  useEffect(() => { load(); }, [load]);

  const update = async (id, patch) => {
    try { await api.patch(`/enquiries/${id}`, patch); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete enquiry?")) return;
    try { await api.delete(`/enquiries/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const exportCsv = async () => {
    try {
      const r = await api.get("/enquiries/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "enquiries.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end gap-4 justify-between">
        <div>
          <div className="text-xs tracking-widest uppercase text-slate-500">Leads</div>
          <h1 className="font-display text-3xl text-[#0B132B]">Enquiries</h1>
        </div>
        <div className="flex gap-2">
          <select value={type} onChange={(e) => setType(e.target.value)} className="h-10 rounded-lg ring-1 ring-slate-200 text-sm px-3">
            <option value="">All Types</option>
            <option value="individual">Individual</option>
            <option value="college">College</option>
            <option value="employer">Employer</option>
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-lg ring-1 ring-slate-200 text-sm px-3">
            {["", "New", "Contacted", "Interested", "Follow-Up", "Enrolled", "Closed"].map((o) => <option key={o} value={o}>{o || "All Status"}</option>)}
          </select>
          <button onClick={exportCsv} className="h-10 px-4 rounded-lg bg-[#0B132B] text-white text-sm">Export CSV</button>
        </div>
      </div>
      <div className="mt-6 rounded-2xl bg-white ring-1 ring-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="p-4">Type</th><th className="p-4">Name / Org</th><th className="p-4">Contact</th>
                <th className="p-4">Interest</th><th className="p-4">Status</th><th className="p-4">Received</th><th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="p-6 text-center text-slate-500">Loading…</td></tr>
              ) : list.length === 0 ? (
                <tr><td colSpan={7} className="p-6 text-center text-slate-500">No enquiries yet.</td></tr>
              ) : list.map((e) => (
                <tr key={e.id} className="border-t border-slate-100">
                  <td className="p-4 uppercase text-[11px] tracking-widest text-slate-500">{e.type || "-"}</td>
                  <td className="p-4">
                    <div className="font-medium text-[#0B132B]">{e.full_name || e.contact_person}</div>
                    <div className="text-xs text-slate-500">{e.college_name || e.company_name || e.user_type}</div>
                  </td>
                  <td className="p-4"><div>{e.phone}</div><div className="text-xs text-slate-500">{e.email}</div></td>
                  <td className="p-4">{e.enquiry_type || e.roles_hiring_for || e.preferred_session || "-"}</td>
                  <td className="p-4">
                    <select value={e.status} onChange={(ev) => update(e.id, { status: ev.target.value })} className="text-xs rounded-md ring-1 ring-slate-200 px-2 py-1">
                      {["New", "Contacted", "Interested", "Follow-Up", "Enrolled", "Closed"].map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="p-4 text-xs text-slate-500">{new Date(e.created_at).toLocaleString()}</td>
                  <td className="p-4"><button onClick={() => remove(e.id)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CrudSection({ endpoint, fields, title }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const preset = PRESET_MAP[endpoint];

  const load = useCallback(async () => {
    const { data } = await api.get(`/content/${endpoint}`); setItems(data);
  }, [endpoint]);
  useEffect(() => { load(); }, [load]);

  const open = (it) => { setEditing(it || {}); setForm(it || {}); };
  const close = () => { setEditing(null); setForm({}); };

  const requiredKeys = fields.filter((f) => f.required).map((f) => f.key);
  const missing = requiredKeys.filter((k) => {
    const v = form?.[k];
    return v === undefined || v === null || String(v).trim() === "";
  });
  const canSave = missing.length === 0;

  const save = async () => {
    if (!canSave) { toast.error(`Please fill: ${missing.join(", ")}`); return; }
    try {
      if (editing?.id) await api.put(`/content/${endpoint}/${editing.id}`, form);
      else await api.post(`/content/${endpoint}`, form);
      toast.success("Saved"); close(); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    try { await api.delete(`/content/${endpoint}/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  return (
    <div>
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs tracking-widest uppercase text-slate-500">Content</div>
          <h1 className="font-display text-3xl text-[#0B132B]">{title}</h1>
        </div>
        <button onClick={() => open({})} className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#0B132B] text-white text-sm">
          <Plus size={14} /> Add {title.slice(0, -1)}
        </button>
      </div>

      <div className="mt-6 grid gap-3">
        {items.map((it) => (
          <div key={it.id} className="rounded-2xl bg-white ring-1 ring-slate-200 p-4 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="font-display text-[#0B132B]">{it.name || it.title || it.question || it.duration}</div>
              <div className="text-xs text-slate-500 truncate">{it.description || it.tagline || it.quote || it.answer || it.biography}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => open(it)} className="h-9 px-3 rounded-lg ring-1 ring-slate-200 text-sm inline-flex items-center gap-2"><Edit3 size={13} /> Edit</button>
              <button onClick={() => remove(it.id)} className="h-9 px-3 rounded-lg ring-1 ring-red-200 text-red-600 text-sm inline-flex items-center gap-2"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-slate-500 text-sm">Nothing here yet.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-[#0B132B]/50 backdrop-blur grid place-items-center p-4" onClick={close}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl p-6 sm:p-8 w-full max-w-2xl max-h-[90vh] overflow-auto shadow-high">
            <div className="font-display text-2xl text-[#0B132B]">{editing.id ? `Edit ${title.slice(0, -1)}` : `New ${title.slice(0, -1)}`}</div>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {fields.map((f) => (
                <FieldRender key={f.key} f={f} value={form[f.key]} onChange={(v) => setField(f.key, v)}
                             full={f.full} preset={IMAGE_KEYS.has(f.key) ? preset : undefined}
                             altValue={IMAGE_KEYS.has(f.key) ? form.image_alt : undefined}
                             onAltChange={IMAGE_KEYS.has(f.key) ? (v) => setField("image_alt", v) : undefined} />
              ))}
            </div>
            {!canSave && (
              <div className="mt-4 text-xs text-red-600">Required: {missing.join(", ")}</div>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={close} className="h-10 px-4 rounded-lg ring-1 ring-slate-200 text-sm">Cancel</button>
              <button onClick={save} disabled={!canSave} className="h-10 px-4 rounded-lg bg-[#0B132B] text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FieldRender({ f, value, onChange, full, preset, altValue, onAltChange }) {
  const cls = "w-full h-11 px-4 rounded-xl bg-white ring-1 ring-slate-200 focus:ring-[#0B132B] focus:outline-none text-sm";
  const wrap = `${full ? "sm:col-span-2" : ""} block`;
  if (IMAGE_KEYS.has(f.key)) {
    return <ImageUploadField label={f.label} value={value} onChange={onChange} testid={`img-${f.key}`}
                              preset={preset} altValue={altValue} onAltChange={onAltChange} />;
  }
  if (f.type === "textarea") {
    return (
      <label className="block sm:col-span-2">
        <span className="text-[11px] uppercase tracking-widest text-slate-500">{f.label}</span>
        <textarea rows={4} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
                  className="mt-1.5 w-full px-4 py-3 rounded-xl bg-white ring-1 ring-slate-200 focus:ring-[#0B132B] focus:outline-none text-sm" />
      </label>
    );
  }
  if (f.type === "list") {
    return (
      <label className="block sm:col-span-2">
        <span className="text-[11px] uppercase tracking-widest text-slate-500">{f.label} (one per line)</span>
        <textarea rows={6} value={(value || []).join("\n")}
                  onChange={(e) => onChange(e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))}
                  className="mt-1.5 w-full px-4 py-3 rounded-xl bg-white ring-1 ring-slate-200 focus:ring-[#0B132B] focus:outline-none text-sm" />
      </label>
    );
  }
  if (f.type === "bool") {
    return (
      <label className="flex items-center gap-2 sm:col-span-2 text-sm">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
        <span>{f.label}</span>
      </label>
    );
  }
  return (
    <label className={wrap}>
      <span className="text-[11px] uppercase tracking-widest text-slate-500">{f.label}</span>
      <input type={f.type || "text"} value={value ?? ""} onChange={(e) => onChange(f.type === "number" ? Number(e.target.value) : e.target.value)}
             className={`mt-1.5 ${cls}`} />
    </label>
  );
}

function SettingsForm({ endpoint, title, fields }) {
  const [data, setData] = useState(null);
  const preset = endpoint.endsWith("/seo") ? "og" : undefined;
  useEffect(() => { api.get(endpoint).then((r) => setData(r.data)); }, [endpoint]);
  if (!data) return <div className="text-slate-500">Loading…</div>;
  const save = async () => {
    try { await api.put(endpoint, data); toast.success("Saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div>
      <div className="text-xs tracking-widest uppercase text-slate-500">Settings</div>
      <h1 className="font-display text-3xl text-[#0B132B]">{title}</h1>
      <div className="mt-6 grid sm:grid-cols-2 gap-4 rounded-2xl bg-white ring-1 ring-slate-200 p-6">
        {fields.map((f) => (
          <FieldRender key={f.key} f={f} value={data[f.key]} onChange={(v) => setData({ ...data, [f.key]: v })} full={f.full}
                       preset={IMAGE_KEYS.has(f.key) ? preset : undefined}
                       altValue={IMAGE_KEYS.has(f.key) ? data.image_alt : undefined}
                       onAltChange={IMAGE_KEYS.has(f.key) ? (v) => setData({ ...data, image_alt: v }) : undefined} />
        ))}
      </div>
      <button onClick={save} className="mt-4 h-10 px-5 rounded-lg bg-[#0B132B] text-white text-sm">Save Changes</button>
    </div>
  );
}

const PROG_FIELDS = [
  { key: "name", label: "Program Name", required: true }, { key: "duration", label: "Duration", required: true },
  { key: "price", label: "Price", required: true }, { key: "badge", label: "Badge (e.g. MOST POPULAR)" },
  { key: "tagline", label: "Tagline", full: true, type: "textarea", required: true },
  { key: "description", label: "Description", full: true, type: "textarea", required: true },
  { key: "features", label: "Features", type: "list" },
  { key: "cta_label", label: "CTA Label" }, { key: "order", label: "Order", type: "number" },
  { key: "active", label: "Active", type: "bool" },
];
const SKILL_FIELDS = [
  { key: "title", label: "Title", required: true }, { key: "icon", label: "Lucide Icon Name (e.g. sparkles)" },
  { key: "description", label: "Description", full: true, type: "textarea", required: true },
  { key: "order", label: "Order", type: "number" }, { key: "active", label: "Active", type: "bool" },
];
const FOUNDER_FIELDS = [
  { key: "name", label: "Name", required: true }, { key: "designation", label: "Designation", required: true },
  { key: "photo_url", label: "Photo URL", full: true },
  { key: "biography", label: "Biography", full: true, type: "textarea", required: true },
  { key: "linkedin_url", label: "LinkedIn URL" }, { key: "instagram_url", label: "Instagram URL" },
  { key: "expertise", label: "Expertise Tags", type: "list" },
  { key: "order", label: "Order", type: "number" },
];
const TEST_FIELDS = [
  { key: "name", label: "Candidate Name", required: true }, { key: "program", label: "Program" },
  { key: "photo_url", label: "Photo URL", full: true },
  { key: "quote", label: "Testimonial", full: true, type: "textarea", required: true },
  { key: "rating", label: "Rating (1-5)", type: "number" },
  { key: "order", label: "Order", type: "number" }, { key: "published", label: "Published", type: "bool" },
];
const FAQ_FIELDS = [
  { key: "question", label: "Question", full: true, required: true },
  { key: "answer", label: "Answer", full: true, type: "textarea", required: true },
  { key: "order", label: "Order", type: "number" }, { key: "published", label: "Published", type: "bool" },
];
const SITE_FIELDS = [
  { key: "hero_headline", label: "Hero Headline", full: true },
  { key: "hero_sub", label: "Hero Sub", full: true, type: "textarea" },
  { key: "hero_tags", label: "Hero Tags", full: true },
  { key: "cta_primary", label: "Primary CTA" }, { key: "cta_secondary", label: "Secondary CTA" },
  { key: "cta_whatsapp", label: "WhatsApp CTA" },
  { key: "phone", label: "Phone" }, { key: "whatsapp", label: "WhatsApp Number (no +)" },
  { key: "email", label: "Email" }, { key: "instagram", label: "Instagram URL" },
  { key: "location", label: "Location" }, { key: "logo_url", label: "Logo URL", full: true },
  { key: "metric_students", label: "Metric — Students" }, { key: "metric_years", label: "Metric — Years" },
  { key: "footer_desc", label: "Footer Description", full: true, type: "textarea" },
];
const SEO_FIELDS = [
  { key: "title", label: "Meta Title", full: true },
  { key: "description", label: "Meta Description", full: true, type: "textarea" },
  { key: "keywords", label: "Keywords", full: true, type: "textarea" },
  { key: "og_image", label: "OG Image URL", full: true },
  { key: "canonical_url", label: "Canonical URL", full: true },
  { key: "ga_id", label: "Google Analytics ID" },
  { key: "gsc_verification", label: "GSC Verification" },
];
